'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Button,
    Switch,
    Checkbox,
} from '@quadminds/mindkit';
import { BadgeCustom } from '@quadminds/mindy-app/components/common/CustomBadge/CustomBadge';
import useTable from '@quadminds/mindy-app/hooks/useTable';
import type { FileItem } from '@quadminds/mindy-app/types/File/File.types';
import { getFileIcon } from '@quadminds/mindy-app/utils/getFileIcon';
import { formatDate } from '@quadminds/mindy-app/utils/utils';
import { MindyKnowledgePermissions } from '@quadminds/mindy-shared';
import { type ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { IAuthMeV2OutputDto } from 'libs/shared/auth-system/src/lib/interfaces/ auth-me-v2-output-dto.interface';
import { FolderIcon, ArrowUpDown, ArchiveX } from 'lucide-react';
import { useMemo } from 'react';
import { ColumnSelector } from './ColumnSelector';
import { TableRowPermissions } from './TableRowPermissions';

import TableWidgets from './TableWidgets.component';

interface FileTableProps {
    readonly files: FileItem[];
    readonly onNavigate: (path: string) => void;
    readonly onMoveFile: (fileId: string, folderId: string) => void;
    readonly onRename: (item: FileItem) => void;
    readonly onDelete: (item: FileItem) => void;
    readonly onInternalDragStart: (fileId: string) => void;
    readonly onInternalDragEnd: () => void;
    readonly draggedFileId: string | null;
    readonly onMoveToFolder: (file: FileItem) => void;
    readonly onToggleActive: (file: FileItem, active: boolean) => void;
    readonly selectedItems: string[];
    readonly onSelectItem: (itemId: string, selected: boolean) => void;
    readonly onSelectAll: (selected: boolean) => void;
    readonly onPreviewFile: (file: FileItem) => void;
    readonly onDownload: (file: FileItem) => void;
    readonly userInfo?: IAuthMeV2OutputDto;
    readonly onReplace: (file: FileItem) => void;
}

export function FileTable({
    files,
    onNavigate,
    onMoveFile,
    onRename,
    onDelete,
    onInternalDragStart,
    onInternalDragEnd,
    draggedFileId,
    onMoveToFolder,
    onToggleActive,
    selectedItems,
    onSelectItem,
    onSelectAll,
    onPreviewFile,
    onDownload,
    userInfo,
    onReplace,
}: FileTableProps) {
    const {
        t,
        sorting,
        setSorting,
        dropTargets,
        containerRef,
        columnVisibility,
        setColumnVisibility,
        currentUserPermissions,
        isImpersonated,
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleSwitchChange,
        handleRowClick,
        allSelected,
        handlePrefetchPermissions,
    } = useTable({
        draggedFileId,
        onInternalDragStart,
        onInternalDragEnd,
        onMoveFile,
        onToggleActive,
        onNavigate,
        onPreviewFile,
        files,
        selectedItems,
    });

    const columns = useMemo<ColumnDef<FileItem>[]>(
        () => [
            {
                id: 'select',
                header: () => (
                    <Checkbox
                        checked={allSelected}
                        onCheckedChange={(checked) => onSelectAll(!!checked)}
                        aria-label="Seleccionar todos"
                        className="mt-1 mr-4"
                        data-testid="select-all-checkbox"
                    />
                ),
                cell: ({ row }) => {
                    const file = row.original;
                    return (
                        <Checkbox
                            checked={selectedItems.includes(file.id)}
                            onCheckedChange={(checked) => onSelectItem(file.id, !!checked)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Seleccionar ${file.name}`}
                            className="mt-1 mr-4"
                            data-testid={`select-document-${file.id}`}
                        />
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'name',
                header: ({ column }) => {
                    return (
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                            className="-ml-4"
                        >
                            {t('knowledgeManager.table.name')}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                    );
                },
                cell: ({ row }) => {
                    const file = row.original;

                    return (
                        <div className="flex items-center space-x-2 cursor-pointer max-w-xs">
                            {file.type === 'folder' ? (
                                <FolderIcon className="h-4 w-4 flex-shrink-0" />
                            ) : (
                                (() => {
                                    const FileTypeIcon = getFileIcon(file.name);
                                    return <FileTypeIcon className="h-4 w-4 text-secondary flex-shrink-0" />;
                                })()
                            )}
                            <span data-testid="file-name" className="truncate" title={file.name}>
                                {file.name}
                            </span>
                        </div>
                    );
                },
                sortingFn: (rowA, rowB) => {
                    const fileA = rowA.original;
                    const fileB = rowB.original;

                    if (fileA.type !== fileB.type) {
                        return fileA.type === 'folder' ? -1 : 1;
                    }

                    return fileA.name.localeCompare(fileB.name);
                },
            },
            {
                accessorKey: 'summary',
                header: ({ column }) => {
                    return (
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                            className="-ml-4"
                        >
                            {t('knowledgeManager.table.summary')}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                    );
                },
                cell: ({ row }) => {
                    return (
                        <div className="line-clamp-2 text-foreground/70 text-xs">{row.original?.summary || '—'}</div>
                    );
                },
            },
            {
                accessorKey: 'comments',
                header: ({ column }) => {
                    return (
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                            className="-ml-4"
                        >
                            {t('knowledgeManager.table.comments')}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                    );
                },
                cell: ({ row }) => {
                    return (
                        <div className="line-clamp-2 text-foreground/70 text-xs">{row.original.comments || '—'}</div>
                    );
                },
            },
            {
                accessorKey: 'active',
                header: t('knowledgeManager.table.active'),
                cell: ({ row }) => {
                    const file = row.original;
                    if (file.type === 'folder') return <div>—</div>;

                    return (
                        <button
                            onClick={(e) => handleSwitchChange(e, file)}
                            className="switch-container"
                            data-testid={`switch-status-${file.id}`}
                        >
                            <Switch
                                checked={file.active || false}
                                className="scale-75"
                                disabled={
                                    (!isImpersonated &&
                                        !currentUserPermissions.includes(MindyKnowledgePermissions.UpdateKnowledge)) ||
                                    file.status === 'analyzing'
                                }
                            />
                        </button>
                    );
                },
            },
            {
                accessorKey: 'status',
                header: t('knowledgeManager.table.status'),
                cell: ({ row }) => {
                    const file = row.original;
                    if (file.type === 'folder') return <div>—</div>;

                    const status = file.status || 'not analyzed';
                    return (
                        <BadgeCustom variant={status === 'analyzed' ? 'success' : 'default'} data-testid="row-status">
                            {t(`knowledgeManager.filters.${status}`)}
                        </BadgeCustom>
                    );
                },
            },
            {
                accessorKey: 'modifiedAt',
                header: ({ column }) => {
                    return (
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                            className="-ml-4"
                        >
                            {t('knowledgeManager.table.lastUpdate')}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                    );
                },
                cell: ({ row }) => {
                    const file = row.original;
                    return (
                        <div>
                            <div className="font-medium">{file.modifiedBy || 'Usuario'}</div>
                            <div className="text-xs text-muted-foreground">
                                {formatDate(file.modifiedAt)}{' '}
                                {file.modifiedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    );
                },
                sortingFn: (rowA, rowB) => {
                    const dateA = rowA.original.modifiedAt.getTime();
                    const dateB = rowB.original.modifiedAt.getTime();
                    return dateA - dateB;
                },
            },
            {
                id: 'actions',
                cell: () => null, // Ya no se usa esta lógica
            },
        ],
        [
            t,
            allSelected,
            onSelectAll,
            selectedItems,
            onSelectItem,
            isImpersonated,
            currentUserPermissions,
            handleSwitchChange,
        ],
    );

    const table = useReactTable({
        data: files,
        columns,
        state: {
            sorting,
            columnVisibility, // Add column visibility to table state
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility, // Add handler for column visibility changes
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div className="w-full" ref={containerRef} data-testid="knowledge-section-table">
            <div className="flex w-full justify-end items-center p-2">
                <TableWidgets className="mr-4" />
                <ColumnSelector table={table} />
            </div>
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.length ? (
                        table.getRowModel().rows.map((row) => {
                            const file = row.original;

                            return (
                                <TableRowPermissions
                                    key={row.id}
                                    row={row}
                                    file={file}
                                    dropTargets={dropTargets}
                                    selectedItems={selectedItems}
                                    onRename={onRename}
                                    onDelete={onDelete}
                                    onMoveToFolder={onMoveToFolder}
                                    onDownload={onDownload}
                                    handleDragStart={handleDragStart}
                                    handleDragEnd={handleDragEnd}
                                    handleDragOver={handleDragOver}
                                    handleDragLeave={handleDragLeave}
                                    handleDrop={handleDrop}
                                    handleRowClick={handleRowClick}
                                    onPrefetchPermissions={handlePrefetchPermissions}
                                    userInfo={userInfo}
                                    onReplace={onReplace}
                                />
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-60 items-center justify-center p-4">
                                <div className="flex flex-col items-center space-y-2 w-full">
                                    <ArchiveX className="text-secondary" />
                                    <span className="text-center text-muted-foreground text-sm">
                                        No hay archivos o carpetas
                                    </span>
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

export default FileTable;
